import { isBlack, isRed } from '../cards.js';
/** What this card adds to a pick-up stack, or null if it is not a penalty card. */
export function penaltyContribution(card, rules) {
    if (card.rank === '2' && rules.twos === 'pickupTwo') {
        return { amount: 2, kind: 'twos' };
    }
    if (card.rank === 'J' && rules.jacks === 'pickupFive' && isBlack(card.suit)) {
        return { amount: 5, kind: 'blackJack' };
    }
    if (card.rank === 'A' && rules.aces === 'heartsPickupFive' && card.suit === 'H') {
        return { amount: 5, kind: 'aceOfHearts' };
    }
    return null;
}
/** Convenience for scoring: how much this card adds, or 0. */
export function penaltyValue(card, rules) {
    return penaltyContribution(card, rules)?.amount ?? 0;
}
/**
 * Which kind of pick-up this card blocks, if any.
 *
 * Blocking is deliberately narrow: the Ace of Diamonds answers the Ace of
 * Hearts and nothing else, and a red Jack answers a black Jack and nothing
 * else. Neither gets a player out of a stack of twos.
 */
export function blocksKind(card, rules) {
    if (card.rank === 'J' && rules.jacks === 'pickupFive' && isRed(card.suit))
        return 'blackJack';
    if (card.rank === 'A' && rules.aces === 'heartsPickupFive' && card.suit === 'D')
        return 'aceOfHearts';
    return null;
}
/** Is this card a blocker at all, regardless of what is on the table? */
export function isPenaltyCanceller(card, rules) {
    return blocksKind(card, rules) !== null;
}
/** Does this card block the stack currently in play? */
export function blocksPending(card, rules, pendingKinds) {
    const kind = blocksKind(card, rules);
    return kind !== null && (pendingKinds?.includes(kind) ?? false);
}
/** Does playing this card let its owner nominate a new suit? */
export function isSuitChanger(card, rules) {
    if (card.rank === 'A' && rules.aces === 'changeSuit')
        return true;
    if (card.rank === 'J' && rules.jacks === 'changeSuit')
        return true;
    return false;
}
/** A suit changer that may be played on anything, ignoring suit and rank. */
export function isWild(card, rules) {
    return rules.suitChangerIsWild && isSuitChanger(card, rules);
}
/** How many turns this card skips. */
export function skipValue(card, rules) {
    if (card.rank === '7' && rules.sevens === 'skip')
        return 1;
    if (card.rank === '8' && rules.eights === 'skip')
        return 1;
    if (card.rank === 'Q' && rules.queens === 'skip')
        return 1;
    return 0;
}
/** Does this card hand the current player another turn? */
export function grantsAnotherTurn(card, rules) {
    return card.rank === '8' && rules.eights === 'playAgain';
}
/** Does this card reverse the direction of play? */
export function reversesDirection(card, rules) {
    return card.rank === 'J' && rules.jacks === 'reverse';
}
/** Does this card force the next player to follow its suit? */
export function setsMirror(card, rules) {
    return card.rank === '7' && rules.sevens === 'mirror';
}
/** A card that does anything beyond being discarded. */
export function isPowerCard(card, rules) {
    return (penaltyContribution(card, rules) !== null ||
        isPenaltyCanceller(card, rules) ||
        isSuitChanger(card, rules) ||
        skipValue(card, rules) > 0 ||
        grantsAnotherTurn(card, rules) ||
        reversesDirection(card, rules) ||
        setsMirror(card, rules));
}
/** Short label for the UI when a pick-up stack is live. */
export function penaltyLabel(card) {
    if (card.rank === '2')
        return 'Twos';
    if (card.rank === 'J')
        return 'Black Jack';
    if (card.rank === 'A')
        return 'Ace of Hearts';
    return 'Pick up';
}
//# sourceMappingURL=effects.js.map